import * as React from 'react';
import useIsHydrated from 'hooks/useIsHydrated';

interface Props {
  /** The address, from constants/internal (COMPANY_EMAIL, SALES_EMAIL). */
  email: string;
  /** Subject line for the new message. */
  subject?: string;
  /** Other `mailto:` fields, already encoded (e.g. `body=Hello%20there`), from CMS links. */
  query?: string;
  /**
   * The link's content. Defaults to the address. Never put the address itself here: it would
   * reach the server HTML.
   */
  children?: React.ReactNode;
  /** Shown instead of the address until the page has hydrated, when there are no children. */
  placeholder?: React.ReactNode;
  /** Shown before the content, e.g. an envelope icon. */
  icon?: React.ReactNode;
  className?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

/**
 * A `mailto:` link whose address is not in the server HTML.
 *
 * Cloudflare's Email Address Obfuscation rewrites every address and `mailto:` link in the HTML it
 * serves, and inserts its decoder script inside the React root. React then cannot match the server
 * HTML: it throws hydration errors #418/#423/#425 and renders the whole page again. So the server
 * and the hydration render show the content (or `placeholder`) without an `href`, and the first
 * render after hydration adds the address. Crawlers that run JavaScript see the full link, and
 * Cloudflare finds no address in the markup. (The address may still be in the JavaScript bundle or
 * the page data, which Cloudflare leaves alone.)
 */
export default function EmailLink({
  email,
  subject,
  query,
  children,
  placeholder = 'Email us',
  icon,
  className,
  target,
  rel,
  onClick,
}: Props) {
  const isHydrated = useIsHydrated();
  const content = children ?? (isHydrated ? email : placeholder);

  if (!isHydrated) {
    return (
      <a className={className}>
        {icon}
        {content}
      </a>
    );
  }

  const fields = [subject ? `subject=${encodeURIComponent(subject)}` : '', query ?? ''].filter(
    Boolean,
  );
  const href = `mailto:${email}${fields.length ? `?${fields.join('&')}` : ''}`;
  return (
    <a href={href} className={className} target={target} rel={rel} onClick={onClick}>
      {icon}
      {content}
    </a>
  );
}
